import { Injectable, inject, signal, computed, effect, OnDestroy } from '@angular/core';
import { Observable, firstValueFrom, from } from 'rxjs';
import { Subject, filter, takeUntil } from 'rxjs';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionRequiredAuthError, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { MsalFactoryService } from './msal-factory.service';
import { Brand } from '../../common/brand-config';
import { environment } from '../../../environments/environment';
import { BrandService } from './brand.service';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {

  private _initializationPromise: Promise<void> | null = null;
  private _isInitialized = false;
  private _isFirstInit = true;
  private _isProcessingRedirectCallback = false;  // Add this flag
  private _intendedRoute: string | null = null;   // Add to preserve route

  async initializeApp(): Promise<void> {
    try {
      const msalService = this.currentMsalService();
      await msalService.instance.initialize();
      const result = await firstValueFrom(
        msalService.handleRedirectObservable()
      );

      if (result) {
        msalService.instance.setActiveAccount(result.account);
        localStorage.setItem('access_token', result.accessToken);

        this.brandService.setAvailableBrandsFromAuthBrand(this._currentAuthType());
        this.brandService.setSelectedBrandWithoutRedirect(this._currentAuthType());

        const storedRoute = sessionStorage.getItem('intended_route');
        const targetRoute =
          storedRoute ||
          this._intendedRoute ||
          '/content/specifications';

        // cleanup
        this._intendedRoute = null;
        sessionStorage.removeItem('intended_route');
        window.location.href = targetRoute;

        return; // stop further init
      }

      // Normal initialization
      this._isInitialized = true;

    } catch (error) {
      console.error('App initialization failed', error);
    }
  }

  /**
   * get the current access token, prefer acquireTokenSilent, return null if failed
   */
  getAccessToken(): Observable<string | null> {
    return from(this.ensureInitialized().then(() => {
      const msalService = this.currentMsalService();
      const accounts = msalService.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) return null;
      const account = msalService.instance.getActiveAccount() || accounts[0];
      const scopes = this.getScopes();
      return msalService.instance.acquireTokenSilent({
        account,
        scopes
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        localStorage.setItem('access_token', result.accessToken);
        return result.accessToken;
      }).catch(() => {
        return null;
      });
    }));
  }

  /**
   * force refresh the token, if fails, logout and throw error
   */
  refreshToken(): Observable<string> {
    return from(this.ensureInitialized().then(async () => {
      const msalService = this.currentMsalService();
      const accounts = msalService.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        // only accounts is empty, means we don't have login state
        // need to clear local state and navigate to login
        this.removeLocalLoginState();
        throw new Error('No account');
      }
      const account = msalService.instance.getActiveAccount() || accounts[0];
      const scopes = this.getScopes();
      try {
        const result = await msalService.instance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh: true
        });
        localStorage.setItem('access_token', result.accessToken);
        return result.accessToken;
      } catch (e) {
        await this.logout();
        throw e;
      }
    }));
  }
  private router = inject(Router);
  private msalFactory = inject(MsalFactoryService);

  private readonly _currentAuthType = signal<Brand>(
    this.getStoredAuthType()
  );

  readonly currentAuthType = this._currentAuthType.asReadonly();

  readonly currentMsalService = computed(() => {
    return this.msalFactory.getMsalService(this._currentAuthType());
  });

  readonly currentDynamicMsalService = computed(() => {
    return this.msalFactory.getDynamicMsalService(this._currentAuthType());
  });

  readonly currentMsalBroadcastService = computed(() => {
    return this.msalFactory.getMsalBroadcastService(this._currentAuthType());
  });


  private readonly _destroying$ = new Subject<void>();

  constructor(private brandService: BrandService) {
    effect(() => {
      const type = this._currentAuthType();
      this.persistAuthType(type);
      this._destroying$.next();
      this.subscribeToMsalEvents();

      // ✅ Only reset to false if NOT the first initialization
      // (i.e., only when user explicitly changes auth type)
      if (!this._isFirstInit) {
        this._isInitialized = false;
        this._initializationPromise = null;
      }
      this._isFirstInit = false;
    });

    this.subscribeToMsalEvents();
  }

  private subscribeToMsalEvents(): void {
    const broadcastService = this.currentMsalBroadcastService();

    broadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => { });

    broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.handleLoginSuccess(result)
      });

    broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGOUT_SUCCESS ||
          msg.eventType === EventType.LOGOUT_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => { });


    broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(async (result: EventMessage) => {
        if (result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          await this.handleTokenRefreshSuccess();
        } else {
          await this.handleTokenRefreshFailure();
        }
      });
  }

  private async handleLoginSuccess(result: EventMessage): Promise<void> {
    if (result.payload) {
      // set account
      const authResult = result.payload as AuthenticationResult;
      this.currentMsalService().instance.setActiveAccount(
        authResult.account
      );
    }
  }

  private async handleTokenRefreshSuccess(): Promise<void> { }

  private async handleTokenRefreshFailure(): Promise<void> { }

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private getStoredAuthType(): Brand {
    try {
      const stored = localStorage.getItem('authType');
      return (stored as Brand) || 'Scania';
    } catch (e) {
      return 'Scania'; //default auth type
    }
  }

  private persistAuthType(type: Brand): void {
    try {
      localStorage.setItem('authType', type);
    } catch (e) {
      console.warn('Cannot save to localStorage', e);
    }
  }

  getCurrentUserRoles(): string[] {
    const msalService = this.currentMsalService();
    const accounts: AccountInfo[] = msalService.instance.getAllAccounts();
    if (accounts && accounts.length > 0) {

      const roles = accounts[0].idTokenClaims?.['roles'] as string[];
      return roles || [];
    }
    return [];
  }

  getScopes() {
    const brand = this._currentAuthType();
    const msalMap = environment.msal as Record<string, { scopes?: string[] } | undefined>;
    const brandConfig = msalMap[brand] ?? msalMap['scania'];
    return brandConfig?.scopes ?? [];
  }

  clearStoredAuthType(): void {
    try {
      localStorage.removeItem('authType');
    } catch (e) {
      console.warn('Cannot clear localStorage', e);
    }
  }

  async setAuthType(type: Brand): Promise<void> {
    if (this._currentAuthType() === type && this._isInitialized) {
      return;
    }

    this._currentAuthType.set(type);
    this._initializationPromise = null;
    await this.initialize();
  }

  async initialize(): Promise<void> {
    if ((window as unknown as Record<string, unknown>)['__karma__']) {
      return;
    }
    if (this._isInitialized) {
      return;
    }

    if (this._initializationPromise) {
      return this._initializationPromise;
    }

    this._initializationPromise = (async () => {
      const msalService = this.currentMsalService();
      try {
        await msalService.instance.initialize();
        await this.handleRedirectCallback();
        this._isInitialized = true;
      } catch (error) {
        this._isInitialized = false;
        throw error;
      } finally {
        this._initializationPromise = null;
      }
    })();

    return this._initializationPromise;
  }

  public async ensureInitialized(): Promise<void> {
    if (!this._isInitialized) {
      await this.initialize();
    }
  }

  async handleRedirectCallback(): Promise<void> {
    const hash = window.location.hash || '';
    const search = window.location.search || '';

    const hasAuthResponse =
      hash.includes('code=') ||
      hash.includes('id_token=') ||
      search.includes('code=') ||
      search.includes('id_token=');

    if (!hasAuthResponse) {
      this.brandService.setAvailableBrandsFromAuthBrand(this._currentAuthType());
      return;
    }

    if (this._isProcessingRedirectCallback) {
      return;
    }

    this._isProcessingRedirectCallback = true;

    try {
      const result = await firstValueFrom(
        this.currentMsalService().handleRedirectObservable()
      );

      if (result) {
        this.currentMsalService().instance.setActiveAccount(result.account);
        this.brandService.setAvailableBrandsFromAuthBrand(this._currentAuthType());
        this.brandService.setSelectedBrandWithoutRedirect(this._currentAuthType());
        localStorage.setItem('access_token', result.accessToken);

        const targetRoute = this._intendedRoute || '/content/specifications';
        this._intendedRoute = null;

        setTimeout(() => {
          this.router.navigateByUrl(targetRoute);
        }, 0);
      }
    } finally {
      this._isProcessingRedirectCallback = false;
    }
  }

  // Add getter to check if redirect callback is being processed
  isProcessingRedirect(): boolean {
    return this._isProcessingRedirectCallback;
  }

  // Add method to store intended route
  setIntendedRoute(route: string): void {
    this._intendedRoute = route;
    sessionStorage.setItem('intended_route', route);
  }

  loginRedirect(loginRequest: RedirectRequest): Promise<void> {
    return this.initialize().then(() => {
      this.currentMsalService().loginRedirect(loginRequest);

      return Promise.resolve();
    });
  }

  async logout() {
    if (this.isLoggedIn()) {
      // remove local login state first
      this.removeLocalLoginState();
      // then call msal logout
      await this.logoutMSAL();
    } else {
      return;
    }
  }

  private removeLocalLoginState() {
    // pay attention: do not clear authType here, because it's user preference
    sessionStorage.clear(); // only clear session storage
    localStorage.clear();
  }

  private async logoutMSAL(): Promise<void> {
    await this.initialize();

    await this.currentMsalService().logoutRedirect({
      postLogoutRedirectUri: '/login'
    });

    this.clearMSALInteractionState();
  }

  // logout and clear authType (authType is user preference, so only clear when full logout)
  fullLogout() {
    this.clearStoredAuthType();
    this.logout();
  }

  isLoggedIn(): boolean {
    try {
      const accounts = this.currentMsalService().instance.getAllAccounts();
      return accounts.length > 0;
    } catch {
      return false;
    }
  }

  async checkLoggedIn(): Promise<boolean> {
    await this.ensureInitialized();
    return this.isLoggedIn();
  }

  async acquireTokenWithRedirect(): Promise<string> {
    await this.ensureInitialized();

    const msalService = this.currentMsalService();
    const accounts = msalService.instance.getAllAccounts();

    if (!accounts || accounts.length === 0) {
      // No user → force login
      await msalService.loginRedirect({
        scopes: this.getScopes(),
      });
      throw new Error('Redirecting to login');
    }

    const account =
      msalService.instance.getActiveAccount() ?? accounts[0];

    try {
      const result = await msalService.instance.acquireTokenSilent({
        account,
        scopes: this.getScopes(),
      });

      localStorage.setItem('access_token', result.accessToken);
      return result.accessToken;

    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msalService.loginRedirect({
          scopes: this.getScopes(),
        });
        throw new Error('Redirecting to login');
      }

      throw error;
    }
  }

  async acquireTokenWithPopup(options?: {
    scopes?: string[];
    claims?: string;
    prompt?: 'login';
  }): Promise<AuthenticationResult> {

    await this.ensureInitialized();

    const instance = this.currentMsalService().instance;
    const account =
      instance.getActiveAccount() ?? instance.getAllAccounts()[0];

    return instance.acquireTokenPopup({
      scopes: options?.scopes ?? this.getScopes(),
      account,
      claims: options?.claims,
      prompt: options?.prompt,
    });
  }

  async getGraphAccessToken(): Promise<string> {
    await this.ensureInitialized();

    const instance = this.currentMsalService().instance;
    const account =
      instance.getActiveAccount() ?? instance.getAllAccounts()[0];

    if (!account) {
      throw new Error('No active account');
    }

    const result = await instance.acquireTokenSilent({
      account,
      scopes: ['User.Read'], // 🔴 GRAPH SCOPE
    });

    return result.accessToken;
  }

  clearMSALInteractionState() {
    try {
      Object.keys(sessionStorage).forEach(key => {
        // Clear all MSAL interaction-related keys
        if (key.includes('msal') || key.includes('interaction') || key.includes('login')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('Failed to clear MSAL interaction state', e);
    }
  }
}